import { useState, useEffect, useCallback } from 'react'
import { format, addDays, subDays } from 'date-fns'
import { fr } from 'date-fns/locale'
import {
  CheckSquare, Square, Plus, Pencil, Trash2, ChevronLeft, ChevronRight,
  ClipboardList, History, Settings2, CheckCircle2, X, Save, RefreshCw,
  Users, UserCheck, UserX, ChevronDown, ChevronUp, RotateCcw, User, Copy, Download, BarChart2, Thermometer, Truck, ShieldCheck, Camera
} from 'lucide-react'
import SignalementModal from '../components/SignalementModal'
import TasksStats from '../components/TasksStats'
import { TemperaturesPanel, ReceptionsPanel } from '../components/QsePanels'
import api from '../lib/api'
import { useEstablishment } from '../contexts/EstablishmentContext'

interface CleanTask {
  id: string; zone1: string; zone2: string; zone3: string
  ordre: number; nom: string; produit: string; photoProduit?: string | null; photoLieu?: string | null; consigne: string
  frequence: string; joursActifs: number[]; prioritesEmployes: string[]; postesRequis: string[]; actif: boolean
  modeAttribution: string; creneauAttribution: string
  employesExclus: string[]; employesFixes: string[]
  employesExceptionnels?: string[]
  dateExceptionnelle?: string
  dateLimite?: string | null
  lourde?: boolean; creneauRepli?: boolean; moisActifs?: number[]; rotation?: string[]
  regleLivraison?: string | null; signalement?: boolean
  rotationActuelle?: string | null; attente?: string | null; enRetard?: boolean; creneauRetenu?: string
  periodKey?: string
  assignedTo?: { id: string; name: string }[]
  validated?: boolean; validatedBy?: string | null; validatedAt?: string | null
  isManuallyAssigned?: boolean
}

interface Employee {
  id: string; firstName: string; lastName: string
  status?: string
  present?: boolean
  excluAttributionAuto?: boolean
  presence?: { heureDebut?: string; heureFin?: string; statut: string }
}

const FREQUENCES = [
  { value: 'jour', label: 'Quotidien' },
  { value: 'semaine', label: 'Hebdomadaire' },
  { value: 'mois', label: 'Mensuel' },
  { value: 'trimestre', label: 'Trimestriel' },
  { value: 'semestre', label: 'Semestriel' },
  { value: 'annee', label: 'Annuel' },
  { value: 'exceptionnelle', label: 'Exceptionnelle' },
]

const CRENEAUX = [
  { value: 'toute_journee', label: 'Toute la journée' },
  { value: 'matin', label: 'Matin' },
  { value: 'apres_midi', label: 'Après-midi' },
]

const FREQ_COLORS: Record<string, string> = {
  jour: 'bg-blue-100 text-blue-700',
  semaine: 'bg-purple-100 text-purple-700',
  mois: 'bg-amber-100 text-amber-700',
  trimestre: 'bg-lime-100 text-lime-700',
  semestre: 'bg-orange-100 text-orange-700',
  annee: 'bg-red-100 text-red-700',
  exceptionnelle: 'bg-pink-100 text-pink-700',
}

const JOURS = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam']

const REGLES_LIVRAISON = [
  { value: '', label: 'Aucune' },
  { value: 'hors_livraison_matin', label: 'Pas le matin d’un jour de livraison' },
  { value: 'veille_livraison', label: 'Seulement la veille d’une livraison' },
  { value: 'jour_livraison', label: 'Seulement un jour de livraison' },
]
const MOIS_LABELS = ['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc']

// ─── Modal Tâche ──────────────────────────────────────────────────────────────

function TaskModal({ task, onClose, onSave, establishmentId }: {
  task: Partial<CleanTask> | null
  onClose: () => void; onSave: () => void
  establishmentId: string
}) {
  const isNew = !task?.id
  const [employees, setEmployees] = useState<Employee[]>([])
  const [positions, setPositions] = useState<{ id: string; name: string; color: string }[]>([])
  useEffect(() => {
    if (!establishmentId) return
    api.get(`/employees?establishmentId=${establishmentId}`).then(r => setEmployees(r.data || [])).catch(() => {})
    api.get('/positions').then(r => setPositions(r.data || [])).catch(() => {})
  }, [establishmentId])
  const [form, setForm] = useState({
    zone1: task?.zone1 || '', zone2: task?.zone2 || '', zone3: task?.zone3 || '',
    ordre: task?.ordre ?? 0, nom: task?.nom || '', produit: task?.produit || '',
    photoProduit: task?.photoProduit || null as string | null,
    photoLieu: task?.photoLieu || null as string | null,
    consigne: task?.consigne || '', frequence: task?.frequence || 'jour',
    joursActifs: task?.joursActifs || [], prioritesEmployes: task?.prioritesEmployes || [],
    postesRequis: task?.postesRequis || [] as string[],
    actif: task?.actif ?? true,
    modeAttribution: task?.modeAttribution || 'equilibre',
    creneauAttribution: task?.creneauAttribution || 'toute_journee',
    employesExclus: task?.employesExclus || [],
    employesFixes: task?.employesFixes || [],
    employesExceptionnels: task?.employesExceptionnels || [],
    dateExceptionnelle: task?.dateExceptionnelle || '',
    dateLimite: task?.dateLimite || '',
    lourde: task?.lourde ?? false,
    creneauRepli: task?.creneauRepli ?? false,
    moisActifs: task?.moisActifs || [] as number[],
    rotationTexte: (task?.rotation || []).join('\n'),
    regleLivraison: task?.regleLivraison || '',
  })
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function handlePhotoChange(e: React.ChangeEvent<HTMLInputElement>, field: 'photoProduit' | 'photoLieu') {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = ev => {
      const img = new Image()
      img.onload = () => {
        const MAX = 300
        const scale = Math.min(MAX / img.width, MAX / img.height, 1)
        const canvas = document.createElement('canvas')
        canvas.width = Math.round(img.width * scale)
        canvas.height = Math.round(img.height * scale)
        canvas.getContext('2d')!.drawImage(img, 0, 0, canvas.width, canvas.height)
        setForm(f => ({ ...f, [field]: canvas.toDataURL('image/jpeg', 0.8) }))
      }
      img.src = ev.target?.result as string
    }
    reader.readAsDataURL(file)
  }

  const toggleNum = (val: number) => setForm(f => ({
    ...f, joursActifs: (f.joursActifs as number[]).includes(val)
      ? (f.joursActifs as number[]).filter(p => p !== val)
      : [...(f.joursActifs as number[]), val]
  }))

  const handleSave = async () => {
    if (!form.nom.trim()) { setError('Le nom est requis'); return }
    setSaving(true); setError('')
    try {
      const { rotationTexte, ...rest } = form
      const payload = { ...rest, rotation: rotationTexte.split(/\r?\n/).map(l => l.trim()).filter(Boolean), dateLimite: form.dateLimite || null }
      if (isNew) await api.post('/tasks', { ...payload, establishmentId })
      else await api.put(`/tasks/${task!.id}`, payload)
      onSave(); onClose()
    } catch (e: any) { setError(e.response?.data?.error || 'Erreur') }
    finally { setSaving(false) }
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-lg font-semibold">{isNew ? 'Nouvelle tâche' : 'Modifier'}</h2>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Nom *</label>
            <input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))}
              className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Ex: Nettoyage sol rayon frais" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            {(['zone1','zone2','zone3'] as const).map((z, i) => (
              <div key={z}>
                <label className="text-xs font-medium text-gray-500">Zone {i+1}</label>
                <input value={form[z]} onChange={e => setForm(f => ({ ...f, [z]: e.target.value }))}
                  className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={['Magasin','Rayon','Détail'][i]} />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-gray-500">Produit</label>
              <input value={form.produit} onChange={e => setForm(f => ({ ...f, produit: e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              <div className="mt-2 flex items-center gap-2">
                {form.photoProduit ? (
                  <div className="relative group inline-block">
                    <img src={form.photoProduit} alt="produit" className="w-16 h-16 object-cover rounded-lg border" />
                    <button type="button" onClick={() => setForm(f => ({ ...f, photoProduit: null }))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                  </div>
                ) : (
                  <label className="cursor-pointer flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 border border-dashed border-blue-300 rounded-lg px-3 py-2 hover:bg-blue-50 transition-colors">
                    📷 Photo produit
                    <input type="file" accept="image/*" className="hidden" onChange={e => handlePhotoChange(e, 'photoProduit')} />
                  </label>
                )}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Créneau</label>
              <select value={form.creneauAttribution} onChange={e => setForm(f => ({ ...f, creneauAttribution: e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {CRENEAUX.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Photo du lieu</label>
            <div className="mt-2 flex items-center gap-2">
              {form.photoLieu ? (
                <div className="relative group inline-block">
                  <img src={form.photoLieu} alt="lieu" className="w-20 h-20 object-cover rounded-lg border border-green-300" />
                  <button type="button" onClick={() => setForm(f => ({ ...f, photoLieu: null }))}
                    className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity">×</button>
                </div>
              ) : (
                <label className="cursor-pointer flex items-center gap-1.5 text-xs text-green-600 hover:text-green-800 border border-dashed border-green-300 rounded-lg px-3 py-2 hover:bg-green-50 transition-colors">
                  📍 Ajouter une photo du lieu de la tâche
                  <input type="file" accept="image/*" className="hidden" onChange={e => handlePhotoChange(e, 'photoLieu')} />
                </label>
              )}
            </div>
          </div>
          {positions.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500">Priorité par poste</label>
              <p className="text-xs text-gray-400 mt-0.5 mb-2">Les employés ayant ce poste seront prioritaires pour cette tâche</p>
              <div className="flex flex-wrap gap-1.5">
                {positions.map(p => {
                  const sel = (form.postesRequis as string[]).includes(p.id)
                  return (
                    <button key={p.id} type="button"
                      onClick={() => setForm(f => ({
                        ...f,
                        postesRequis: sel
                          ? (f.postesRequis as string[]).filter(id => id !== p.id)
                          : [...(f.postesRequis as string[]), p.id]
                      }))}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${sel ? 'text-white border-transparent' : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'}`}
                      style={sel ? { backgroundColor: p.color, borderColor: p.color } : {}}>
                      {sel ? '✓ ' : ''}{p.name}
                    </button>
                  )
                })}
              </div>
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500">Consigne</label>
            <textarea value={form.consigne} onChange={e => setForm(f => ({ ...f, consigne: e.target.value }))}
              rows={2} className="mt-1 w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="text-xs font-medium text-gray-500">Attribution</label>
            <div className="mt-1 flex gap-2 flex-wrap">
              {[{v:'equilibre',l:'Auto — équilibré'},{v:'strict',l:'Auto — strict'},{v:'manuel',l:'Employés fixes'}].map(m => (
                <button key={m.v} type="button" onClick={() => setForm(f => ({ ...f, modeAttribution: m.v }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${form.modeAttribution === m.v ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>
                  {m.l}
                </button>
              ))}
            </div>
            {form.modeAttribution === 'manuel' && (
              <div className="mt-2">
                <p className="text-xs text-gray-400 mb-1.5">
                  Sélectionner un ou plusieurs employés :
                  {form.employesFixes.length > 1 && <span className="ml-1 text-blue-600 font-medium">👥 Tâche partagée ({form.employesFixes.length} personnes)</span>}
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {employees.map(e => {
                    const sel = (form.employesFixes as string[]).includes(e.id)
                    return (
                      <button key={e.id} type="button"
                        onClick={() => setForm(f => ({
                          ...f,
                          employesFixes: sel
                            ? (f.employesFixes as string[]).filter(id => id !== e.id)
                            : [...(f.employesFixes as string[]), e.id]
                        }))}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${sel ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}`}>
                        {e.firstName}
                      </button>
                    )
                  })}
                  {employees.length === 0 && <p className="text-xs text-gray-400 italic">Aucun employé actif</p>}
                </div>
              </div>
            )}
            {form.modeAttribution === 'strict' && (
              <div className="mt-2">
                <p className="text-xs text-gray-400 mb-1.5">Ordre de priorité (1er = prioritaire) :</p>
                <div className="space-y-1">
                  {employees.map(e => {
                    const prioList = form.prioritesEmployes as string[]
                    const pos = prioList.indexOf(e.id)
                    const rank = pos >= 0 ? pos + 1 : null
                    return (
                      <div key={e.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${rank !== null ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}>
                        <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${rank !== null ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'}`}>
                          {rank ?? '—'}
                        </span>
                        <span className="flex-1">{e.firstName} {e.lastName}</span>
                        <div className="flex gap-1">
                          {rank !== null && rank > 1 && (
                            <button type="button" onClick={() => setForm(f => {
                              const list = [...(f.prioritesEmployes as string[])]
                              const i = list.indexOf(e.id)
                              ;[list[i - 1], list[i]] = [list[i], list[i - 1]]
                              return { ...f, prioritesEmployes: list }
                            })} className="p-1 text-gray-400 hover:text-blue-600">▲</button>
                          )}
                          {rank !== null && rank < prioList.length && (
                            <button type="button" onClick={() => setForm(f => {
                              const list = [...(f.prioritesEmployes as string[])]
                              const i = list.indexOf(e.id)
                              ;[list[i], list[i + 1]] = [list[i + 1], list[i]]
                              return { ...f, prioritesEmployes: list }
                            })} className="p-1 text-gray-400 hover:text-blue-600">▼</button>
                          )}
                          <button type="button" onClick={() => setForm(f => {
                            const list = f.prioritesEmployes as string[]
                            return {
                              ...f,
                              prioritesEmployes: list.includes(e.id)
                                ? list.filter(id => id !== e.id)
                                : [...list, e.id]
                            }
                          })} className={`px-2 py-0.5 rounded text-xs font-medium border ${rank !== null ? 'bg-red-50 text-red-600 border-red-200 hover:bg-red-100' : 'bg-white text-gray-500 border-gray-200 hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200'}`}>
                            {rank !== null ? 'Retirer' : '+ Ajouter'}
                          </button>
                        </div>
                      </div>
                    )
                  })}
                  {employees.length === 0 && <p className="text-xs text-gray-400 italic">Aucun employé actif</p>}
                </div>
              </div>
            )}
          </div>
          {form.modeAttribution !== 'exceptionnelle' && employees.length > 0 && (
            <div>
              <label className="text-xs font-medium text-gray-500">Employés exclus de cette tâche</label>
              <div className="mt-1 flex flex-wrap gap-1.5">
                {employees.map(e => {
                  const exclu = (form.employesExclus as string[]).includes(e.id)
                  return (
                    <button key={e.id} type="button"
                      onClick={() => setForm(f => ({
                        ...f,
                        employesExclus: exclu
                          ? (f.employesExclus as string[]).filter(id => id !== e.id)
                          : [...(f.employesExclus as string[]), e.id]
                      }))}
                      className={`px-3 py-1.5 rounded-full text-sm border transition-all ${exclu ? 'bg-red-100 text-red-700 border-red-300' : 'bg-white text-gray-600 border-gray-200 hover:border-red-300'}`}>
                      {exclu ? '🚫 ' : ''}{e.firstName} {e.lastName}
                    </button>
                  )
                })}
              </div>
              {(form.employesExclus as string[]).length > 0 && (
                <p className="text-xs text-red-500 mt-1">{(form.employesExclus as string[]).length} employé(s) exclu(s) de l'attribution auto</p>
              )}
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-gray-500">Fréquence</label>
            <div className="mt-1 flex gap-2 flex-wrap">
              {FREQUENCES.map(f => (
                <button key={f.value} onClick={() => setForm(prev => ({ ...prev, frequence: f.value, joursActifs: [] }))}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium border ${form.frequence === f.value ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-700 border-gray-200'}`}>
                  {f.label}
                </button>
              ))}
            </div>
            {form.frequence === 'jour' && (
              <p className="mt-2 text-xs text-gray-500">Jours de la semaine : aucun coché = tous les jours (ex. livraisons : cocher seulement les jours de livraison)</p>
            )}
            {(form.frequence === 'semaine' || form.frequence === 'jour') && (
              <div className="mt-2 flex gap-1">
                {JOURS.map((j, i) => (
                  <button key={i} onClick={() => toggleNum(i)}
                    className={`w-9 h-9 rounded-lg text-xs font-medium border ${(form.joursActifs as number[]).includes(i) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                    {j}
                  </button>
                ))}
              </div>
            )}
            {form.frequence === 'mois' && (
              <input type="number" min={1} max={31}
                value={(form.joursActifs as number[])[0] || ''}
                onChange={e => setForm(f => ({ ...f, joursActifs: e.target.value ? [parseInt(e.target.value)] : [] }))}
                className="mt-2 w-24 border rounded-lg px-3 py-2 text-sm" placeholder="Jour 1-31" />
            )}
            {(form.frequence === 'semestre' || form.frequence === 'trimestre') && (
              <div className="mt-2 flex gap-1 flex-wrap">
                {MOIS_LABELS.map((m, i) => (
                  <button key={i} onClick={() => toggleNum(i + 1)}
                    className={`px-2 py-1 rounded text-xs font-medium border ${(form.joursActifs as number[]).includes(i + 1) ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                    {m}
                  </button>
                ))}
              </div>
            )}
            {form.frequence === 'annee' && (
              <div className="mt-2 flex items-center gap-2">
                <select
                  value={(form.joursActifs as number[])[0] || ''}
                  onChange={e => setForm(f => {
                    const month = e.target.value ? parseInt(e.target.value) : undefined
                    const day = (f.joursActifs as number[])[1]
                    return { ...f, joursActifs: month ? [month, day || 1] : [] }
                  })}
                  className="border rounded-lg px-2 py-2 text-sm">
                  <option value="">Mois</option>
                  {['Jan','Fév','Mar','Avr','Mai','Jun','Jul','Aoû','Sep','Oct','Nov','Déc'].map((m, i) => (
                    <option key={i} value={i + 1}>{m}</option>
                  ))}
                </select>
                <input type="number" min={1} max={31}
                  value={(form.joursActifs as number[])[1] || ''}
                  onChange={e => setForm(f => {
                    const month = (f.joursActifs as number[])[0] || 1
                    return { ...f, joursActifs: e.target.value ? [month, parseInt(e.target.value)] : [month] }
                  })}
                  className="w-20 border rounded-lg px-3 py-2 text-sm" placeholder="Jour" />
              </div>
            )}
            {form.frequence === 'exceptionnelle' && (
              <div className="mt-2 space-y-2">
                <div>
                  <label className="text-xs text-gray-500">Date de la tâche exceptionnelle</label>
                  <input type="date" value={form.dateExceptionnelle}
                    onChange={e => setForm(f => ({ ...f, dateExceptionnelle: e.target.value }))}
                    className="mt-1 block border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">À faire au plus tard le (vide = seulement ce jour-là)</label>
                  <input type="date" value={form.dateLimite}
                    onChange={e => setForm(f => ({ ...f, dateLimite: e.target.value }))}
                    className="mt-1 block border rounded-lg px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="text-xs text-gray-500">Employé(s) désigné(s) (1–2 max ; aucun = les présents)</label>
                  <div className="mt-1 flex flex-wrap gap-1.5">
                    {employees.map(e => {
                      const sel = (form.employesExceptionnels as string[]).includes(e.id)
                      const maxReached = (form.employesExceptionnels as string[]).length >= 2 && !sel
                      return (
                        <button key={e.id} type="button" disabled={maxReached}
                          onClick={() => setForm(f => ({
                            ...f,
                            employesExceptionnels: sel
                              ? (f.employesExceptionnels as string[]).filter(id => id !== e.id)
                              : [...(f.employesExceptionnels as string[]), e.id]
                          }))}
                          className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${sel ? 'bg-pink-600 text-white border-pink-600' : maxReached ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed' : 'bg-white text-gray-700 border-gray-300 hover:border-pink-400'}`}>
                          {e.firstName} {e.lastName}
                        </button>
                      )
                    })}
                    {employees.length === 0 && <p className="text-xs text-gray-400 italic">Aucun employé actif</p>}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-3 border-t pt-3">
            <p className="text-xs font-semibold text-gray-600">Plan de ménage</p>
            <div>
              <label className="text-xs font-medium text-gray-500">Mois où la tâche existe (aucun coché = toute l’année)</label>
              <div className="mt-1 flex gap-1 flex-wrap">
                {MOIS_LABELS.map((m, i) => {
                  const on = (form.moisActifs as number[]).includes(i + 1)
                  return (
                    <button key={i} type="button"
                      onClick={() => setForm(f => ({ ...f, moisActifs: on ? (f.moisActifs as number[]).filter(x => x !== i + 1) : [...(f.moisActifs as number[]), i + 1] }))}
                      className={`px-2 py-1 rounded text-xs font-medium border ${on ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-600 border-gray-200'}`}>
                      {m}
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-500">Selon les livraisons</label>
              <select value={form.regleLivraison} onChange={e => setForm(f => ({ ...f, regleLivraison: e.target.value }))}
                className="mt-1 block w-full border rounded-lg px-3 py-2 text-sm">
                {REGLES_LIVRAISON.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <label className="flex items-start gap-2 text-sm text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={form.lourde} onChange={e => setForm(f => ({ ...f, lourde: e.target.checked }))} />
              <span>Tâche lourde : seulement quand il y a au moins 3 personnes sur le créneau</span>
            </label>
            {form.creneauAttribution === 'apres_midi' && (
              <label className="flex items-start gap-2 text-sm text-gray-700">
                <input type="checkbox" className="mt-0.5" checked={form.creneauRepli} onChange={e => setForm(f => ({ ...f, creneauRepli: e.target.checked }))} />
                <span>Si personne ne peut la faire l’après-midi, la donner au matin</span>
              </label>
            )}
            <div>
              <label className="text-xs font-medium text-gray-500">Tour de rôle (une ligne par élément, ex. Frigo 1, Frigo 2…)</label>
              <textarea value={form.rotationTexte} rows={3}
                onChange={e => setForm(f => ({ ...f, rotationTexte: e.target.value }))}
                className="mt-1 w-full border rounded-lg px-3 py-2 text-sm" placeholder="Vide = pas de tour de rôle" />
            </div>
          </div>
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
        <div className="flex justify-end gap-3 px-5 py-4 border-t bg-gray-50 rounded-b-2xl">
          <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600">Annuler</button>
          <button onClick={handleSave} disabled={saving}
            className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-5 py-2 rounded-lg disabled:opacity-50">
            <Save size={14} /> {saving ? 'Enregistrement...' : 'Enregistrer'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Modal réattribution ──────────────────────────────────────────────────────

function ReassignModal({ task, employees, onConfirm, onClose }: {
  task: CleanTask
  employees: Employee[]
  onConfirm: (ids: string[]) => void
  onClose: () => void
}) {
  const isBinome = ['semestre', 'semestriel', 'annee', 'annuel'].includes(task.frequence)
  const [selectedIds, setSelectedIds] = useState<string[]>(task.assignedTo?.map(a => a.id) || [])
  const presentEmployees = employees.filter(e => e.present)

  const toggleSel = (id: string) =>
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id])

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b">
          <div>
            <h2 className="font-semibold">Attribuer à...</h2>
            {isBinome && <p className="text-xs text-gray-400 mt-0.5">Tâche binôme — sélectionner 1 ou 2 personnes</p>}
          </div>
          <button onClick={onClose}><X size={20} className="text-gray-400" /></button>
        </div>
        <div className="p-4 space-y-2">
          {presentEmployees.map(emp => {
            const sel = selectedIds.includes(emp.id)
            return (
              <button key={emp.id}
                onClick={() => isBinome ? toggleSel(emp.id) : onConfirm([emp.id])}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl border transition-colors text-left ${sel ? 'bg-blue-50 border-blue-300' : 'hover:bg-gray-50 border-gray-200'}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${sel ? 'bg-blue-600 text-white' : 'bg-blue-100 text-blue-700'}`}>
                  {sel ? '✓' : `${emp.firstName?.[0] || ''}${emp.lastName && emp.lastName !== '-' ? emp.lastName[0] : ''}`}
                </div>
                <span className="font-medium text-sm">{emp.firstName} {emp.lastName && emp.lastName !== '-' ? emp.lastName : ''}</span>
              </button>
            )
          })}
          {presentEmployees.length === 0 && (
            <p className="text-center py-4 text-gray-400 text-sm">Aucun employé présent</p>
          )}
        </div>
        {isBinome && selectedIds.length > 0 && (
          <div className="px-4 pb-4">
            <button
              onClick={() => onConfirm(selectedIds)}
              className="w-full bg-blue-600 text-white font-semibold py-3 rounded-xl text-sm">
              Confirmer ({selectedIds.length} personne{selectedIds.length > 1 ? 's' : ''})
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Composant principal ───────────────────────────────────────────────────────

export default function Tasks() {
  const [tab, setTab] = useState<'today' | 'presences' | 'manage' | 'history' | 'suivi' | 'temperatures' | 'livraisons'>('today')
  const [freqTab, setFreqTab] = useState<'jour' | 'semaine' | 'mois' | 'trimestre' | 'semestre' | 'annee' | 'exceptionnelle'>('jour')
  const [signalOpen, setSignalOpen] = useState(false)
  const [date, setDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [todayData, setTodayData] = useState<{ tasks: CleanTask[]; employees: Employee[] }>({ tasks: [], employees: [] })
  const [allTasks, setAllTasks] = useState<CleanTask[]>([])
  const [history, setHistory] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [modalTask, setModalTask] = useState<Partial<CleanTask> | null | undefined>(undefined)
  const [collapsedZones, setCollapsedZones] = useState<Record<string, boolean>>({})
  const [dashStats, setDashStats] = useState<{ done: number; total: number; percent: number } | null>(null)
  const [reassignModal, setReassignModal] = useState<{ task: CleanTask } | null>(null)
  const [savingPresence, setSavingPresence] = useState<string | null>(null)
  const [allEmployees, setAllEmployees] = useState<Employee[]>([])
  const [globalExclusions, setGlobalExclusions] = useState<string[]>([])
  const [allPositions, setAllPositions] = useState<{ id: string; name: string; color: string }[]>([])

  const { current: establishment } = useEstablishment()
  const userRole = JSON.parse(localStorage.getItem('user') || '{}')?.role || ''
  const isManager = ['super_admin', 'admin', 'manager'].includes(userRole)
  const isAdminUp = ['super_admin', 'admin'].includes(userRole)

  const loadToday = useCallback(async () => {
    if (!establishment) return
    setLoading(true)
    try {
      const r = await api.get(`/tasks/today?establishmentId=${establishment.id}&date=${date}`)
      setTodayData({ tasks: r.data.tasks || [], employees: r.data.employees || [] })
    } catch { } finally { setLoading(false) }
  }, [establishment, date])

  const loadAll = useCallback(async () => {
    if (!establishment) return
    // Chargement indépendant pour éviter qu'une erreur bloque tout
    api.get(`/tasks?establishmentId=${establishment.id}`)
      .then(r => setAllTasks(r.data))
      .catch(e => console.error('[loadAll/tasks]', e))
    api.get(`/tasks/global-exclusions?establishmentId=${establishment.id}`)
      .then(r => setGlobalExclusions(r.data.exclusions || []))
      .catch(e => console.error('[loadAll/exclusions]', e))
    api.get('/positions').then(r => setAllPositions(r.data || [])).catch(() => {})
    api.get(`/employees?establishmentId=${establishment.id}`)
      .then(r => { console.log('[loadAll/employees]', r.data); setAllEmployees(Array.isArray(r.data) ? r.data : []) })
      .catch(e => console.error('[loadAll/employees]', e))
  }, [establishment])

  const loadAllEmployees = useCallback(async () => {
    if (!establishment) return
    api.get(`/employees?establishmentId=${establishment.id}`)
      .then(r => { console.log('[loadAllEmployees]', r.data); setAllEmployees(Array.isArray(r.data) ? r.data : []) })
      .catch(e => console.error('[loadAllEmployees]', e))
  }, [establishment])

  const loadHistory = useCallback(async () => {
    if (!establishment) return
    try {
      const to = format(new Date(), 'yyyy-MM-dd')
      const from = format(subDays(new Date(), 30), 'yyyy-MM-dd')
      const r = await api.get(`/tasks/history?establishmentId=${establishment.id}&from=${from}&to=${to}`)
      setHistory(r.data)
    } catch { }
  }, [establishment])

  const loadDash = useCallback(async () => {
    if (!establishment) return
    try {
      const r = await api.get(`/tasks/dashboard?establishmentId=${establishment.id}`)
      setDashStats(r.data.today)
    } catch { }
  }, [establishment])

  // Synchronise allEmployees depuis todayData (chargé au démarrage) + appel direct au besoin
  useEffect(() => {
    if (todayData.employees.length > 0) setAllEmployees(todayData.employees)
  }, [todayData.employees])

  useEffect(() => { loadDash() }, [loadDash])
  // Charge les employés dès que l'établissement est connu (sans attendre le tab manage)
  useEffect(() => { loadAllEmployees() }, [loadAllEmployees])
  useEffect(() => {
    if (tab === 'today' || tab === 'presences') loadToday()
    else if (tab === 'manage') { loadAll(); loadAllEmployees() }
    else if (tab === 'history') loadHistory()
  }, [tab, loadToday, loadAll, loadHistory, loadAllEmployees])

  const validate = async (task: CleanTask) => {
    await api.post(`/tasks/${task.id}/validate`, { date })
    loadToday(); loadDash()
  }

  const unvalidate = async (task: CleanTask) => {
    await api.delete(`/tasks/${task.id}/validate?date=${date}`)
    loadToday(); loadDash()
  }

  const togglePresence = async (emp: Employee) => {
    if (!establishment) return
    setSavingPresence(emp.id)
    try {
      const newStatut = emp.present ? 'absent' : 'present'
      await api.post('/tasks/presences', {
        establishmentId: establishment.id,
        date, employeeId: emp.id, statut: newStatut
      })
      loadToday()
    } finally { setSavingPresence(null) }
  }

  const toggleExclu = async (emp: Employee) => {
    try {
      const r = await api.patch(`/tasks/employees/${emp.id}/exclu`)
      setTodayData(prev => ({
        ...prev,
        employees: prev.employees.map(e =>
          e.id === emp.id ? { ...e, excluAttributionAuto: r.data.excluAttributionAuto } : e
        )
      }))
    } catch {}
  }

  const deleteTask = async (id: string) => {
    if (!confirm('Désactiver cette tâche ?')) return
    await api.delete(`/tasks/${id}`)
    loadAll()
  }

  const duplicateTask = async (id: string) => {
    await api.post(`/tasks/${id}/duplicate`)
    loadAll()
  }

  const importQse = async () => {
    if (!establishment?.id) return
    if (!confirm(`Importer les tâches QSE du magasin ${establishment.name} (dates, lave-mains, surgelés, poids du pain, contrôle des livraisons) ?
Les tâches déjà présentes ne sont pas recréées.`)) return
    try {
      const r = await api.post('/qse/seed/tasks', { establishmentId: establishment.id })
      alert(`✅ ${r.data.created} tâche(s) QSE créée(s), ${r.data.updated} déjà présente(s)`)
      loadAll()
    } catch (e: any) { alert(e?.response?.data?.error || "Erreur lors de l'import") }
  }

  const importHaccp = async () => {
    if (!establishment?.id) return
    if (!confirm('Importer les 12 tâches du protocole HACCP Ecolab (Libre-Service Produits Frais) ?\nVous pourrez les modifier ensuite.')) return
    try {
      const r = await api.post('/tasks/seed-haccp', { establishmentId: establishment.id })
      alert(`✅ ${r.data.created} tâches importées avec succès !`)
      loadAll()
    } catch { alert('Erreur lors de l\'import') }
  }

  const importSurfaceVente = async () => {
    if (!establishment?.id) return
    if (!confirm('Importer les tâches Surface de Vente + Réserve + Zones Techniques ?\nVous pourrez les modifier ensuite.')) return
    try {
      const r = await api.post('/tasks/seed-surface-vente', { establishmentId: establishment.id })
      alert(`✅ ${r.data.created} tâches importées, ${r.data.updated} mises à jour !`)
      loadAll()
    } catch { alert('Erreur lors de l\'import') }
  }

  const reassign = async (taskId: string, employeeIds: string[]) => {
    await api.post(`/tasks/${taskId}/reassign`, { date, employeeIds })
    setReassignModal(null); loadToday()
  }

  const resetReassign = async (taskId: string) => {
    await api.delete(`/tasks/${taskId}/reassign?date=${date}`)
    loadToday()
  }

  const toggleGlobalExclusion = async (employeeId: string) => {
    if (!establishment) return
    const next = globalExclusions.includes(employeeId)
      ? globalExclusions.filter(id => id !== employeeId)
      : [...globalExclusions, employeeId]
    setGlobalExclusions(next)
    await api.post('/tasks/global-exclusions', { establishmentId: establishment.id, exclusions: next })
    loadToday()
  }

  const grouped = (tasks: CleanTask[]) => {
    const map: Record<string, CleanTask[]> = {}
    for (const t of tasks) {
      const z = t.zone1 || 'Sans zone'
      if (!map[z]) map[z] = []
      map[z].push(t)
    }
    return map
  }

  const { tasks: todayTasks, employees } = todayData
  const doneTasks = todayTasks.filter(t => t.validated)
  const presentCount = employees.filter(e => e.present).length

  const tabs = [
    { key: 'today', icon: <CheckSquare size={14} />, label: "Tâches du jour" },
    ...(isManager ? [{ key: 'presences', icon: <Users size={14} />, label: `Présences (${presentCount})` }] : []),
    ...(isManager ? [{ key: 'manage', icon: <Settings2 size={14} />, label: 'Gérer' }] : []),
    { key: 'suivi', icon: <BarChart2 size={14} />, label: 'Suivi' },
    ...(isManager ? [{ key: 'temperatures', icon: <Thermometer size={14} />, label: 'Températures' }] : []),
    ...(isManager ? [{ key: 'livraisons', icon: <Truck size={14} />, label: 'Livraisons' }] : []),
    { key: 'history', icon: <History size={14} />, label: 'Historique' },
  ]

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {/* Header */}
      <div className="flex-shrink-0 bg-white border-b px-6 py-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ClipboardList size={22} className="text-blue-600" /> UtileClean
            </h1>
            <p className="text-sm text-gray-500">Tâches récurrentes & hygiène</p>
          </div>
          {dashStats !== null && (
            <div className="flex items-center gap-3">
              <div className="text-center">
                <div className="text-2xl font-bold">{dashStats.done}/{dashStats.total}</div>
                <div className="text-xs text-gray-500">Aujourd'hui</div>
              </div>
              <div className="w-14 h-14 relative">
                <svg viewBox="0 0 36 36" className="w-14 h-14 -rotate-90">
                  <circle cx="18" cy="18" r="15.9" fill="none" stroke="#e5e7eb" strokeWidth="3" />
                  <circle cx="18" cy="18" r="15.9" fill="none"
                    stroke={dashStats.percent === 100 ? '#16a34a' : '#2563eb'}
                    strokeWidth="3" strokeDasharray={`${dashStats.percent} 100`} strokeLinecap="round" />
                </svg>
                <span className="absolute inset-0 flex items-center justify-center text-xs font-bold">{dashStats.percent}%</span>
              </div>
            </div>
          )}
        </div>
        <div className="flex gap-1 mt-4 bg-gray-100 rounded-xl p-1 w-fit flex-wrap">
          {tabs.map(t => (
            <button key={t.key} onClick={() => setTab(t.key as any)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${tab === t.key ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'}`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-auto p-6">

        {/* ── PRÉSENCES ─────────────────────────────────────────────────────── */}
        {tab === 'presences' && (
          <>
            <div className="flex items-center gap-3 mb-5">
              <button onClick={() => setDate(format(subDays(new Date(date), 1), 'yyyy-MM-dd'))} className="p-2 rounded-lg bg-white border hover:bg-gray-50"><ChevronLeft size={16} /></button>
              <span className="font-semibold capitalize">{format(new Date(date), 'EEEE d MMMM', { locale: fr })}</span>
              <button onClick={() => setDate(format(addDays(new Date(date), 1), 'yyyy-MM-dd'))} className="p-2 rounded-lg bg-white border hover:bg-gray-50"><ChevronRight size={16} /></button>
              <button onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))} className="text-sm text-blue-600 hover:underline">Aujourd'hui</button>
            </div>
            <div className="bg-white rounded-xl border overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b flex items-center justify-between">
                <span className="font-semibold text-sm text-gray-700">{employees.length} employé(s)</span>
                <span className="text-sm text-green-600 font-medium">{presentCount} présent(s)</span>
              </div>
              <div className="divide-y">
                {employees.map(emp => (
                  <div key={emp.id} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${emp.present ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-400'}`}>
                        {emp.firstName[0]}{emp.lastName[0]}
                      </div>
                      <div>
                        <p className="font-medium text-sm text-gray-900">{emp.firstName} {emp.lastName}</p>
                        {emp.presence?.heureDebut && (
                          <p className="text-xs text-gray-400">{emp.presence.heureDebut} — {emp.presence.heureFin || '?'}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => togglePresence(emp)} disabled={savingPresence === emp.id}
                        className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${emp.present ? 'bg-green-100 text-green-700 hover:bg-red-100 hover:text-red-700' : 'bg-gray-100 text-gray-500 hover:bg-green-100 hover:text-green-700'}`}>
                        {emp.present ? <UserCheck size={14} /> : <UserX size={14} />}
                        {savingPresence === emp.id ? '...' : emp.present ? 'Présent' : 'Absent'}
                      </button>
                      <button
                        onClick={() => toggleExclu(emp)}
                        title={emp.excluAttributionAuto ? 'Exclu de l\'auto-attribution — cliquer pour réactiver' : 'Inclus dans l\'auto-attribution — cliquer pour exclure'}
                        className={`px-2 py-1.5 rounded-lg text-xs font-medium border transition-colors ${emp.excluAttributionAuto ? 'bg-red-100 text-red-700 border-red-200 hover:bg-red-200' : 'bg-white text-gray-400 border-gray-200 hover:bg-gray-50'}`}>
                        {emp.excluAttributionAuto ? '🚫 Exclu' : '✓ Auto'}
                      </button>
                    </div>
                  </div>
                ))}
                {employees.length === 0 && (
                  <p className="text-center py-8 text-gray-400 text-sm">Aucun employé dans cet établissement</p>
                )}
              </div>
            </div>
          </>
        )}

        {/* ── TÂCHES DU JOUR ────────────────────────────────────────────────── */}
        {tab === 'today' && (
          <>
            {/* Résumé par fréquence */}
            {/* Sous-onglets fréquence */}
            <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
              {([
                { freq: 'jour',     label: 'Journalières',  activeColor: 'bg-blue-600 text-white',   dotColor: 'bg-blue-400' },
                { freq: 'semaine',  label: 'Hebdomadaires', activeColor: 'bg-purple-600 text-white',  dotColor: 'bg-purple-400' },
                { freq: 'mois',     label: 'Mensuelles',    activeColor: 'bg-amber-500 text-white',   dotColor: 'bg-amber-400' },
                { freq: 'trimestre', label: 'Trimestrielles', activeColor: 'bg-lime-600 text-white',  dotColor: 'bg-lime-400' },
                { freq: 'semestre', label: 'Semestrielles', activeColor: 'bg-orange-500 text-white',  dotColor: 'bg-orange-400' },
                { freq: 'annee',    label: 'Annuelles',     activeColor: 'bg-red-500 text-white',     dotColor: 'bg-red-400' },
                { freq: 'exceptionnelle', label: 'Signalements', activeColor: 'bg-pink-600 text-white', dotColor: 'bg-pink-400' },
              ] as const).map(({ freq, label, activeColor, dotColor }) => {
                const totalToday = todayTasks.filter(t => t.frequence === freq).length
                const doneToday  = todayTasks.filter(t => t.frequence === freq && t.validated).length
                const active = freqTab === freq
                return (
                  <button key={freq} onClick={() => setFreqTab(freq)}
                    className={`flex-shrink-0 flex flex-col items-center px-4 py-2.5 rounded-xl border text-sm font-semibold transition-all ${active ? activeColor + ' border-transparent shadow' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-300'}`}>
                    <span>{label}</span>
                    {totalToday > 0 ? (
                      <span className={`text-xs mt-0.5 font-medium ${active ? 'text-white/80' : 'text-gray-400'}`}>{doneToday}/{totalToday}</span>
                    ) : (
                      <span className={`text-xs mt-0.5 ${active ? 'text-white/60' : 'text-gray-300'}`}>{allTasks.filter(t => t.actif && t.frequence === freq).length} tâche(s)</span>
                    )}
                  </button>
                )
              })}
            </div>

            <div className="flex items-center gap-3 mb-5">
              <button onClick={() => setDate(format(subDays(new Date(date), 1), 'yyyy-MM-dd'))} className="p-2 rounded-lg bg-white border hover:bg-gray-50"><ChevronLeft size={16} /></button>
              <span className="font-semibold capitalize">{format(new Date(date), 'EEEE d MMMM yyyy', { locale: fr })}</span>
              <button onClick={() => setDate(format(addDays(new Date(date), 1), 'yyyy-MM-dd'))} className="p-2 rounded-lg bg-white border hover:bg-gray-50"><ChevronRight size={16} /></button>
              <button onClick={() => setDate(format(new Date(), 'yyyy-MM-dd'))} className="text-sm text-blue-600 hover:underline">Aujourd'hui</button>
              <button onClick={loadToday} className="p-2 rounded-lg bg-white border hover:bg-gray-50"><RefreshCw size={14} /></button>
              {isManager && establishment && (
                <button onClick={() => setSignalOpen(true)}
                  className="ml-auto flex items-center gap-1.5 bg-pink-600 text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-pink-700">
                  <Camera size={14} /> Signaler
                </button>
              )}
            </div>
            {signalOpen && establishment && (
              <SignalementModal establishmentId={establishment.id} onClose={() => setSignalOpen(false)}
                onDone={() => { setSignalOpen(false); setFreqTab('exceptionnelle'); loadToday() }} />
            )}

            {presentCount === 0 && isManager && !loading && (
              <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-xl flex items-center gap-3">
                <UserX size={16} className="text-amber-600 flex-shrink-0" />
                <p className="text-sm text-amber-800">
                  Aucune présence enregistrée — <button onClick={() => setTab('presences')} className="underline font-medium">Gérer les présences</button> pour que l'attribution automatique fonctionne.
                </p>
              </div>
            )}

            {loading ? (
              <div className="text-center py-12 text-gray-400">Chargement...</div>
            ) : todayTasks.filter(t => t.frequence === freqTab).length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <CheckCircle2 size={48} className="mx-auto mb-3 text-gray-300" />
                <p>Aucune tâche {freqTab === 'jour' ? 'journalière' : freqTab === 'semaine' ? 'hebdomadaire' : freqTab === 'mois' ? 'mensuelle' : freqTab === 'trimestre' ? 'trimestrielle' : freqTab === 'semestre' ? 'semestrielle' : freqTab === 'exceptionnelle' ? 'signalée' : 'annuelle'} prévue ce jour</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Barre de progression */}
                {(() => {
                  const freqTasks = todayTasks.filter(t => t.frequence === freqTab)
                  const freqDone = freqTasks.filter(t => t.validated).length
                  return (
                    <div className="bg-white rounded-xl border p-4">
                      <div className="flex justify-between text-sm text-gray-600 mb-2">
                        <span><b>{freqDone}</b> validée(s)</span>
                        <span><b>{freqTasks.length - freqDone}</b> restante(s)</span>
                      </div>
                      <div className="h-2 bg-gray-100 rounded-full">
                        <div className="h-2 bg-green-500 rounded-full transition-all"
                          style={{ width: `${freqTasks.length > 0 ? (freqDone / freqTasks.length) * 100 : 0}%` }} />
                      </div>
                    </div>
                  )
                })()}

                {Object.entries(grouped(todayTasks.filter(t => t.frequence === freqTab))).map(([zone, tasks]) => (
                  <div key={zone} className="bg-white rounded-xl border overflow-hidden">
                    <button onClick={() => setCollapsedZones(prev => ({ ...prev, [zone]: !prev[zone] }))}
                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100">
                      <span className="font-semibold text-gray-800">{zone}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">{tasks.filter(t => t.validated).length}/{tasks.length}</span>
                        {collapsedZones[zone] ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
                      </div>
                    </button>
                    {!collapsedZones[zone] && (
                      <div className="divide-y">
                        {tasks.map(task => (
                          <div key={task.id}
                            className={`flex items-start gap-3 px-4 py-3 ${task.validated ? 'bg-green-50' : 'hover:bg-gray-50'}`}>
                            <button onClick={() => task.validated ? (isManager && unvalidate(task)) : validate(task)}
                              className="mt-0.5 flex-shrink-0">
                              {task.validated
                                ? <CheckCircle2 size={22} className="text-green-500" />
                                : <Square size={22} className="text-gray-300 hover:text-blue-500" />}
                            </button>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className={`font-medium text-sm ${task.validated ? 'line-through text-gray-400' : 'text-gray-900'}`}>{task.nom}</span>
                                {task.zone2 && <span className="text-xs text-gray-400">{task.zone2}{task.zone3 ? ` › ${task.zone3}` : ''}</span>}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FREQ_COLORS[task.frequence] || 'bg-gray-100 text-gray-600'}`}>
                                  {FREQUENCES.find(f => f.value === task.frequence)?.label}
                                </span>
                                {(task.creneauRetenu || task.creneauAttribution) !== 'toute_journee' && (
                                  <span className="text-xs text-gray-400 italic">{(task.creneauRetenu || task.creneauAttribution) === 'matin' ? 'Matin' : 'Après-midi'}</span>
                                )}
                                {task.rotationActuelle && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-teal-100 text-teal-700">↻ {task.rotationActuelle}</span>
                                )}
                                {task.lourde && <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-slate-100 text-slate-600">Lourde</span>}
                                {task.attente === 'lourde' && !task.validated && (
                                  <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-700">En attente : moins de 3 personnes sur le créneau</span>
                                )}
                                {task.dateLimite && !task.validated && (
                                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${task.enRetard ? 'bg-red-100 text-red-700' : 'bg-pink-50 text-pink-700'}`}>
                                    {task.enRetard ? 'En retard, prévu le' : 'Avant le'} {format(new Date(task.dateLimite), 'd MMM', { locale: fr })}
                                  </span>
                                )}
                                {task.postesRequis?.length > 0 && task.postesRequis.map(pid => {
                                  const pos = allPositions.find(p => p.id === pid)
                                  return pos ? (
                                    <span key={pid} className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                                      style={{ backgroundColor: pos.color }}>
                                      🎯 {pos.name}
                                    </span>
                                  ) : null
                                })}
                              </div>
                              {(task.produit || task.photoProduit || task.photoLieu) && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  {task.photoProduit && (
                                    <img src={task.photoProduit} alt={task.produit || 'produit'}
                                      className="w-10 h-10 object-cover rounded-lg border flex-shrink-0 cursor-pointer hover:scale-150 transition-transform"
                                      title={task.produit || 'Photo du produit'} />
                                  )}
                                  {task.photoLieu && (
                                    <img src={task.photoLieu} alt="lieu"
                                      className="w-10 h-10 object-cover rounded-lg border border-green-300 flex-shrink-0 cursor-pointer hover:scale-150 transition-transform"
                                      title="Photo du lieu" />
                                  )}
                                  {task.produit && <p className="text-xs text-gray-500">Produit : {task.produit}</p>}
                                </div>
                              )}
                              {task.consigne && (
                                <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                                  {task.consigne.split('\n').map((line, i) => <p key={i}>{line}</p>)}
                                </div>
                              )}

                              {/* Attribution */}
                              {!task.validated && task.assignedTo && task.assignedTo.length > 0 && (
                                <div className="flex items-center gap-1.5 mt-1.5">
                                  <User size={11} className="text-blue-500" />
                                  <span className="text-xs text-blue-700 font-medium">
                                    {task.assignedTo.map(a => a.name).join(' / ')}
                                  </span>
                                  {task.isManuallyAssigned && <span className="text-xs text-amber-600 bg-amber-50 px-1 rounded">manuel</span>}
                                  {isManager && (
                                    <div className="flex gap-1 ml-1">
                                      <button onClick={() => setReassignModal({ task })}
                                        className="text-xs text-gray-400 hover:text-blue-600 underline">Changer</button>
                                      {task.isManuallyAssigned && (
                                        <button onClick={() => resetReassign(task.id)}
                                          className="text-xs text-gray-400 hover:text-gray-700 underline flex items-center gap-0.5">
                                          <RotateCcw size={10} /> Auto
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                              {task.validated && task.validatedBy && (
                                <p className="text-xs text-green-600 mt-1">✓ {task.validatedBy} — {task.validatedAt ? format(new Date(task.validatedAt), 'HH:mm') : ''}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── GÉRER ─────────────────────────────────────────────────────────── */}
        {tab === 'manage' && (
          <>
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">{allTasks.filter(t => t.actif).length} tâche(s) active(s)</p>
              <div className="flex items-center gap-2">
                <button onClick={importSurfaceVente}
                  className="flex items-center gap-2 border border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100 text-sm font-medium px-3 py-2 rounded-lg">
                  <Download size={14} /> Surface Vente + Réserve
                </button>
                {isAdminUp && (
                  <button onClick={importQse}
                    className="flex items-center gap-2 border border-emerald-300 text-emerald-700 bg-emerald-50 hover:bg-emerald-100 text-sm font-medium px-3 py-2 rounded-lg">
                    <ShieldCheck size={14} /> Tâches QSE
                  </button>
                )}
                <button onClick={importHaccp}
                  className="flex items-center gap-2 border border-teal-300 text-teal-700 bg-teal-50 hover:bg-teal-100 text-sm font-medium px-3 py-2 rounded-lg">
                  <Download size={14} /> Protocole HACCP Ecolab
                </button>
                <button onClick={() => setModalTask({})}
                  className="flex items-center gap-2 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-lg">
                  <Plus size={16} /> Nouvelle tâche
                </button>
              </div>
            </div>
            {/* Exclusions globales */}
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-4">
              <p className="text-sm font-semibold text-amber-800 mb-3">Exclusions globales — exclus de toutes les tâches</p>
              <div className="flex flex-wrap gap-2">
                {allEmployees.map(e => {
                  const excluded = globalExclusions.includes(e.id)
                  return (
                    <button key={e.id} onClick={() => toggleGlobalExclusion(e.id)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium border transition-colors ${excluded ? 'bg-red-100 border-red-300 text-red-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}`}>
                      <span className={`w-2 h-2 rounded-full ${excluded ? 'bg-red-500' : 'bg-green-400'}`} />
                      {e.firstName} {e.lastName}
                    </button>
                  )
                })}
                {allEmployees.length === 0 && <p className="text-sm text-amber-600 italic">Chargement...</p>}
              </div>
              <p className="text-xs text-amber-600 mt-2">Rouge = exclu de toutes les tâches · Vert = inclus normalement</p>
            </div>

            {allTasks.filter(t => t.actif).length === 0 ? (
              <div className="text-center py-16 text-gray-400">
                <ClipboardList size={48} className="mx-auto mb-3 text-gray-300" />
                <p className="mb-4">Aucune tâche</p>
                <button onClick={() => setModalTask({})} className="bg-blue-600 text-white px-5 py-2 rounded-lg text-sm">Créer</button>
              </div>
            ) : (
              <div className="space-y-2">
                {Object.entries(grouped(allTasks.filter(t => t.actif))).map(([zone, tasks]) => (
                  <div key={zone} className="bg-white rounded-xl border overflow-hidden">
                    <div className="px-4 py-2 bg-gray-50 flex items-center justify-between">
                      <span className="font-semibold text-sm text-gray-700">{zone}</span>
                      <span className="text-xs text-gray-400">{tasks.filter(t => t.actif).length} tâche(s)</span>
                    </div>
                    <div className="divide-y">
                      {tasks.map(task => (
                        <div key={task.id} className="flex items-center gap-3 px-4 py-3">
                          <div className="flex-1 min-w-0 flex items-center gap-3">
                            {task.photoProduit && (
                              <img src={task.photoProduit} alt={task.produit || 'produit'}
                                className="w-10 h-10 object-cover rounded-lg border flex-shrink-0"
                                title={task.produit || 'Photo du produit'} />
                            )}
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-medium text-sm">{task.nom}</span>
                                {task.zone2 && <span className="text-xs text-gray-400">{task.zone2}{task.zone3 ? ` › ${task.zone3}` : ''}</span>}
                                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${FREQ_COLORS[task.frequence] || 'bg-gray-100 text-gray-600'}`}>
                                  {FREQUENCES.find(f => f.value === task.frequence)?.label}
                                </span>
                                <span className="text-xs text-gray-400">{CRENEAUX.find(c => c.value === task.creneauAttribution)?.label}</span>
                                <span className={`text-xs px-1.5 py-0.5 rounded ${task.modeAttribution === 'strict' ? 'bg-red-50 text-red-600' : 'bg-green-50 text-green-600'}`}>
                                  {task.modeAttribution === 'strict' ? 'Strict' : 'Équilibré'}
                                </span>
                              </div>
                              {task.produit && <p className="text-xs text-gray-500 mt-0.5">Produit : {task.produit}</p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-1">
                            <button onClick={() => setModalTask(task)} title="Modifier" className="p-1.5 text-gray-400 hover:text-blue-600 rounded-lg"><Pencil size={14} /></button>
                            <button onClick={() => duplicateTask(task.id)} title="Dupliquer" className="p-1.5 text-gray-400 hover:text-green-600 rounded-lg"><Copy size={14} /></button>
                            <button onClick={() => deleteTask(task.id)} title="Désactiver" className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg"><Trash2 size={14} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ── HISTORIQUE ────────────────────────────────────────────────────── */}
        {tab === 'suivi' && establishment && <TasksStats establishmentId={establishment.id} />}
        {tab === 'temperatures' && establishment && <TemperaturesPanel establishmentId={establishment.id} />}
        {tab === 'livraisons' && establishment && <ReceptionsPanel establishmentId={establishment.id} />}

        {tab === 'history' && (
          <>
            <p className="text-sm text-gray-500 mb-4">30 derniers jours</p>
            {history.length === 0 ? (
              <div className="text-center py-16 text-gray-400"><History size={48} className="mx-auto mb-3 text-gray-300" /><p>Aucune validation</p></div>
            ) : (
              <div className="bg-white rounded-xl border overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                    <tr>
                      <th className="text-left px-4 py-3">Date</th>
                      <th className="text-left px-4 py-3">Tâche</th>
                      <th className="text-left px-4 py-3">Zone</th>
                      <th className="text-left px-4 py-3">Validé par</th>
                      <th className="text-left px-4 py-3">Heure</th>
                      <th className="text-left px-4 py-3">Photo</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {history.map((v: any) => (
                      <tr key={v.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2">{format(new Date(v.date), 'dd/MM/yyyy')}</td>
                        <td className="px-4 py-2 font-medium">{v.task?.nom}</td>
                        <td className="px-4 py-2 text-gray-500">{v.task?.zone1}</td>
                        <td className="px-4 py-2">{v.employeeName || '—'}</td>
                        <td className="px-4 py-2 text-gray-500">{format(new Date(v.validatedAt), 'HH:mm')}</td>
                        <td className="px-4 py-2">
                          {v.hasPhoto && (
                            <button className="text-blue-600 hover:underline text-xs"
                              onClick={async () => {
                                const r = await api.get(`/tasks/validations/${v.id}/photo`)
                                const w = window.open('')
                                if (w) w.document.write(`<img src="${r.data.photo}" style="max-width:100%">`)
                              }}>📷 voir</button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal tâche */}
      {modalTask !== undefined && (
        <TaskModal task={modalTask} establishmentId={establishment?.id || ''}
          onClose={() => setModalTask(undefined)} onSave={loadAll} />
      )}

      {/* Modal réattribution */}
      {reassignModal && (
        <ReassignModal
          task={reassignModal.task}
          employees={employees}
          onConfirm={(ids) => { reassign(reassignModal.task.id, ids); setReassignModal(null) }}
          onClose={() => setReassignModal(null)}
        />
      )}
    </div>
  )
}
